// Keeps the match across a page reload. The save is the match record: the
// seed and the actions. A load replays it, so the board, the
// log, and the history all return. A save that does not load is ignored.

import { MATCH_QUERY_PARAM, STORAGE_KEY_MATCH } from '../constants';
import { decodeMatch, isVersionedMatch, type VersionedMatch, withVersion } from '../game/matchLink';
import type { MatchRecord } from '../game/replay';
import { readJson, writeJson } from '../lib/storage';
import { gameStore, loadMatch, matchRecord } from './game';
import { isMatchWaiting, matchWaitStore } from './matchWait';
import { markNarrated } from './narrator';
import { isInRoom } from './roomStore';
import { markRollShown } from './stage';

/** The rolls, casts, and events of a loaded match count as shown, so the stage and the narrator stay quiet. */
export const loadMatchQuietly = (record: MatchRecord): boolean => {
  try {
    return loadMatch(record, (state) => {
      markRollShown(state.lastRoll);
      markNarrated(state);
    });
  } catch {
    // The record is from older rules or is damaged.
    return false;
  }
};

/**
 * Loads the match in the `match` parameter of the URL. False when there is none or it
 * does not load. The parameter leaves the URL, so a reload continues from the save.
 * `npm run try` makes such a link. See `ai/scenario.ts`.
 */
export const restoreLinkedMatch = (): boolean => {
  const url = new URL(window.location.href);
  const text = url.searchParams.get(MATCH_QUERY_PARAM);
  if (text === null) return false;
  url.searchParams.delete(MATCH_QUERY_PARAM);
  window.history.replaceState(null, '', url);
  const record = decodeMatch(text);
  return record !== null && loadMatchQuietly(record);
};

/** Loads the saved match into the game store. False when there is none or it does not load. */
export const restoreSavedMatch = (): boolean => {
  const saved = readJson<Partial<VersionedMatch> | null>(STORAGE_KEY_MATCH, null);
  return isVersionedMatch(saved) && loadMatchQuietly(saved);
};

/** Call once at startup, after the first match is in the store. */
export const startMatchSave = (): void => {
  // A match that waits for its casters is no match yet. A reload asks for them again.
  // The match of a room is on the server, and the save keeps the match against the AI.
  const save = (): void => {
    if (!isMatchWaiting() && !isInRoom()) {
      writeJson(STORAGE_KEY_MATCH, withVersion(matchRecord()));
    }
  };
  save();
  gameStore.subscribe(save);
  // The match that the pick started is in the store before the wait ends.
  matchWaitStore.subscribe(save);
};
